import { create } from "zustand";

/**
 * Phase 18 WU-18.D5 — Ops Console live state.
 *
 * - Initial render comes from `GET /api/ops/snapshot` → `replaceFromSnapshot`.
 * - Live WebSocket events (`operator_metrics_tick`, `batch_sla_tick`,
 *   `sla_approaching`, `document:slaBreached`, `bottleneck_detected`)
 *   update the store granularly via the specific upsert methods.
 * - `reconnecting` flips true when the WS drops for >30s. The Ops Console
 *   renders a banner and starts 15s polling until the WS comes back.
 */

export type SlaStatus = "ok" | "approaching" | "breached";

export interface OperatorLive {
  operatorId: string;
  name: string;
  currentDocId: string | null;
  currentStep: string | null;
  kpm5m: number;
  docsThisSession: number;
  activeSince: number;
}

export interface BatchLive {
  batchId: string;
  jobTypeName: string;
  docCount: number;
  currentStep: string | null;
  elapsedMs: number;
  slaTargetMs: number | null;
  slaStatus: SlaStatus;
}

export interface BottleneckEntry {
  step: string;
  stallCount: number;
  avgMs: number;
  historicalAvgMs: number;
}

interface OpsState {
  snapshotAt: number;
  operators: Record<string, OperatorLive>;
  batches: Record<string, BatchLive>;
  bottlenecks: BottleneckEntry[];
  queueDepths: Record<string, number>;
  reconnecting: boolean;

  upsertOperatorTick: (t: {
    operatorId: string;
    kpm5m: number;
    currentDocId: string;
    currentStep: string;
  }) => void;
  upsertBatchTick: (t: {
    batchId: string;
    elapsedMs: number;
    slaStatus: SlaStatus;
  }) => void;
  addBottleneck: (b: BottleneckEntry) => void;
  replaceFromSnapshot: (snap: {
    operators: OperatorLive[];
    batches: BatchLive[];
    queueDepths: Record<string, number>;
  }) => void;
  setReconnecting: (v: boolean) => void;
}

export const useOpsStore = create<OpsState>((set) => ({
  snapshotAt: 0,
  operators: {},
  batches: {},
  bottlenecks: [],
  queueDepths: {},
  reconnecting: false,

  upsertOperatorTick: (t) =>
    set((s) => {
      const existing = s.operators[t.operatorId];
      const merged: OperatorLive = existing
        ? {
            ...existing,
            kpm5m: t.kpm5m,
            currentDocId: t.currentDocId,
            currentStep: t.currentStep,
          }
        : {
            operatorId: t.operatorId,
            name: t.operatorId,
            currentDocId: t.currentDocId,
            currentStep: t.currentStep,
            kpm5m: t.kpm5m,
            docsThisSession: 0,
            activeSince: Date.now(),
          };
      return { operators: { ...s.operators, [t.operatorId]: merged } };
    }),

  upsertBatchTick: (t) =>
    set((s) => {
      const existing = s.batches[t.batchId];
      if (!existing) return s;
      return {
        batches: {
          ...s.batches,
          [t.batchId]: {
            ...existing,
            elapsedMs: t.elapsedMs,
            slaStatus: t.slaStatus,
          },
        },
      };
    }),

  addBottleneck: (b) =>
    set((s) => {
      const filtered = s.bottlenecks.filter((x) => x.step !== b.step);
      return { bottlenecks: [...filtered, b] };
    }),

  replaceFromSnapshot: (snap) =>
    set(() => ({
      snapshotAt: Date.now(),
      operators: Object.fromEntries(snap.operators.map((o) => [o.operatorId, o])),
      batches: Object.fromEntries(snap.batches.map((b) => [b.batchId, b])),
      queueDepths: snap.queueDepths,
    })),

  setReconnecting: (v) => set({ reconnecting: v }),
}));
